var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useMemo, useState } from "react";
import { Button, Checkbox, MessageBanner, ModalFooter, AccordionSet, Accordion, Row, Col, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller, useFieldArray, } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "react-query";
import { NaturalPersonLegalFormEnum, PersonIdentifierTypeEnum, } from "../../types/Api";
import { createNaturalPerson } from "../../api/naturalPersonService";
import { createInlineNaturalPersonSchema } from "../../utils/validationUtils";
import { NaturalPersonGeneralFields } from "../NaturalPersonGeneralFields";
import { LocalOnlyCheckbox } from "../LocalOnlyCheckbox";
import { RecekTextField, RecekSelect } from "../inputs";
import { useAccordion } from "../../utils/useAccordion";
import { useAuthoritySendErrorCallout } from "../../hooks/useAuthoritySendErrorCallout";
import { buildPersonInvalidSubmitContent } from "../../utils/personInvalidSubmit";
import css from "./InlineModal.module.css";
var defaultValues = {
    legalForm: NaturalPersonLegalFormEnum.NATURAL,
    firstName: "",
    lastName: "",
    birthDate: "",
    deathDate: "",
    biographicalNote: "",
    disclosureConsent: true,
    localOnly: false,
    identifiers: [],
    naturalAlternativeNames: [],
};
export var CreateNaturalPersonForm = function (_a) {
    var onCreated = _a.onCreated, onCancel = _a.onCancel;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useState(null), submitError = _b[0], setSubmitError = _b[1];
    var _c = useState(null), invalidSubmitWarning = _c[0], setInvalidSubmitWarning = _c[1];
    var notifySendError = useAuthoritySendErrorCallout().notifySendError;
    var schema = useMemo(function () { return createInlineNaturalPersonSchema(formatMessage); }, [formatMessage]);
    var _d = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaultValues,
        mode: "onBlur",
    }), control = _d.control, handleSubmit = _d.handleSubmit, watch = _d.watch;
    var _e = useAccordion({
        identifiers: true,
        alternativeNames: true,
    }), accordionStatus = _e.accordionStatus, toggleAccordion = _e.toggleAccordion;
    var _f = useFieldArray({
        control: control,
        name: "identifiers",
    }), identifierFields = _f.fields, appendIdentifier = _f.append, removeIdentifier = _f.remove;
    var _g = useFieldArray({
        control: control,
        name: "naturalAlternativeNames",
    }), alternativeNameFields = _g.fields, appendAlternativeName = _g.append, removeAlternativeName = _g.remove;
    var watchedDeathDate = watch("deathDate");
    var watchedLocalOnly = watch("localOnly");
    var _h = useMutation(function (values) {
        return createNaturalPerson(ky, __assign(__assign({}, values), { legalForm: NaturalPersonLegalFormEnum.NATURAL }));
    }, {
        onSuccess: function (created) {
            queryClient.invalidateQueries(["personSearchOptions"]);
            queryClient.invalidateQueries(["natural-persons"]);
            notifySendError(created);
            onCreated(created);
        },
        onError: function () {
            setSubmitError(formatMessage({
                id: "ui-br-agency.title.contributions.form.createPerson.error",
            }));
        },
    }), mutate = _h.mutate, isLoading = _h.isLoading;
    var onSubmit = function (values) {
        setSubmitError(null);
        setInvalidSubmitWarning(null);
        mutate(values);
    };
    var onInvalid = function (formErrors) {
        setInvalidSubmitWarning(buildPersonInvalidSubmitContent(formatMessage, formErrors));
    };
    return (React.createElement(React.Fragment, null,
        submitError && (React.createElement(MessageBanner, { type: "error", className: css.errorBanner }, submitError)),
        invalidSubmitWarning && (React.createElement(MessageBanner, { type: "warning", className: css.errorBanner }, invalidSubmitWarning)),
        React.createElement("form", { noValidate: true, onSubmit: handleSubmit(onSubmit, onInvalid) },
            React.createElement(NaturalPersonGeneralFields, { control: control, layout: "compact", biographicalNoteRequired: !watchedLocalOnly }),
            React.createElement(LocalOnlyCheckbox, { control: control }),
            !watchedDeathDate && (React.createElement(Controller, { control: control, name: "disclosureConsent", render: function (_a) {
                    var _b;
                    var _c = _a.field, value = _c.value, onChange = _c.onChange, fieldState = _a.fieldState;
                    return (React.createElement(Checkbox, { label: formatMessage({
                            id: "ui-br-agency.natural-person.disclosureConsent.field.label",
                        }), checked: !!value, onChange: function (e) { return onChange(e.target.checked); }, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message }));
                } })),
            React.createElement(AccordionSet, null,
                React.createElement(Accordion, { label: formatMessage({
                        id: "ui-br-agency.natural-person.accordions.label.identifiers",
                    }), open: accordionStatus.identifiers, onToggle: function () { return toggleAccordion("identifiers"); } },
                    identifierFields.map(function (identifierField, index) { return (React.createElement(Row, { key: identifierField.id, style: { marginBottom: "15px" } },
                        React.createElement(Col, { xs: 12, md: 5 },
                            React.createElement(Controller, { control: control, name: "identifiers.".concat(index, ".type"), render: function (_a) {
                                    var _b;
                                    var field = _a.field, fieldState = _a.fieldState;
                                    return (React.createElement(RecekSelect, __assign({}, field, { labelId: "ui-br-agency.natural-person.identifier.type.field.label", dataOptions: [
                                            {
                                                value: PersonIdentifierTypeEnum.ISNI,
                                                label: formatMessage({
                                                    id: "ui-br-agency.natural-person.identifier.type.isni",
                                                }) || "ISNI",
                                            },
                                            {
                                                value: PersonIdentifierTypeEnum.ORCID,
                                                label: formatMessage({
                                                    id: "ui-br-agency.natural-person.identifier.type.orcid",
                                                }) || "ORCID",
                                            },
                                        ], error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                                } })),
                        React.createElement(Col, { xs: 12, md: 5 },
                            React.createElement(Controller, { control: control, name: "identifiers.".concat(index, ".identifier"), render: function (_a) {
                                    var _b;
                                    var field = _a.field, fieldState = _a.fieldState;
                                    return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-agency.natural-person.identifier.value.field.label", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                                } })),
                        React.createElement(Col, { xs: 12, md: 2, style: { display: "flex", alignItems: "flex-end" } },
                            React.createElement(Button, { buttonStyle: "danger", onClick: function () { return removeIdentifier(index); }, style: { marginBottom: "15px" } },
                                React.createElement(FormattedMessage, { id: "ui-br-agency.button.delete" }))))); }),
                    React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement(Button, { onClick: function () {
                                    return appendIdentifier({
                                        type: PersonIdentifierTypeEnum.ISNI,
                                        identifier: "",
                                    }, { shouldFocus: false });
                                } },
                                React.createElement(FormattedMessage, { id: "ui-br-agency.natural-person.button.addIdentifier" }))))),
                React.createElement(Accordion, { label: formatMessage({
                        id: "ui-br-agency.natural-person.accordions.label.alternativeNames",
                    }), open: accordionStatus.alternativeNames, onToggle: function () { return toggleAccordion("alternativeNames"); } },
                    alternativeNameFields.map(function (field, index) { return (React.createElement(Row, { key: field.id, style: { marginBottom: "15px" } },
                        React.createElement(Col, { xs: 12, md: 5 },
                            React.createElement(Controller, { control: control, name: "naturalAlternativeNames.".concat(index, ".firstName"), render: function (_a) {
                                    var _b;
                                    var field = _a.field, fieldState = _a.fieldState;
                                    return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-agency.natural-person.alternativeName.firstName.field.label", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                                } })),
                        React.createElement(Col, { xs: 12, md: 5 },
                            React.createElement(Controller, { control: control, name: "naturalAlternativeNames.".concat(index, ".lastName"), render: function (_a) {
                                    var _b;
                                    var field = _a.field, fieldState = _a.fieldState;
                                    return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-agency.natural-person.alternativeName.lastName.field.label", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                                } })),
                        React.createElement(Col, { xs: 12, md: 2, style: { display: "flex", alignItems: "flex-end" } },
                            React.createElement(Button, { buttonStyle: "danger", onClick: function () { return removeAlternativeName(index); }, style: { marginBottom: "15px" } },
                                React.createElement(FormattedMessage, { id: "ui-br-agency.button.delete" }))))); }),
                    React.createElement(Row, null,
                        React.createElement(Col, { xs: 12 },
                            React.createElement(Button, { onClick: function () {
                                    return appendAlternativeName({ firstName: "", lastName: "" });
                                } },
                                React.createElement(FormattedMessage, { id: "ui-br-agency.natural-person.button.addAlternativeName" })))))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit(onSubmit, onInvalid), disabled: isLoading },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.save" })),
                React.createElement(Button, { buttonStyle: "default", onClick: onCancel, disabled: isLoading },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))))));
};
